import { getDisplayQuantity, getEstimateNumberValueBasedOnSettings, getMeasurementDetails } from "@Actions/estimation";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { useRef } from "react";
import { useTranslation } from "@i18n";
import { useSelector } from "react-redux";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { RootState } from "@Reducers/index";
import type { EstimateData } from "@ServerTypes/estimates/estimates.schema";
import type {
  ColumnDefinition,
  GridRowBase,
  SlickGrid1Ref,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";

/** A derived-measurement row: one viewer markup plus its raw measurement value. */
type DerivedMeasurementRow = GridRowBase & {
  id: string | number;
  _name: string;
  _measurementValue: number;
  _unitMultiplier: number;
  units?: string;
};

/** This grid's columns omit `id` and carry SlickGrid's legacy `textAlign` flag. */
type DerivedMeasurementColumn = Omit<ColumnDefinition<DerivedMeasurementRow>, "id"> & { textAlign?: string };

/** The SlickGrid formatter signature, bound to this grid's row type. */
type DerivedMeasurementFormatter = NonNullable<ColumnDefinition<DerivedMeasurementRow>["formatter"]>;

type DeriveMeasurementsProps = {
  dataSource: DerivedMeasurementRow[];
  derivedMeasurementsSelectingEvents: (records: DerivedMeasurementRow[]) => void;
  selectedMeasurements: DerivedMeasurementRow[];
};

type LoadedDisplayUnit = NonNullable<EstimateData["linearDisplayUnit"]> & {
  factor: number;
  selectedSymbol: { name: string };
  type: { name: string };
};
type LoadedEstimateData = EstimateData &
  Record<
    "linearDisplayUnit" | "areaDisplayUnit" | "volumeDisplayUnit" | "countDisplayUnit" | "angleDisplayUnit",
    LoadedDisplayUnit
  >;

const DeriveMeasurements = ({
  dataSource,
  derivedMeasurementsSelectingEvents,
  selectedMeasurements,
}: DeriveMeasurementsProps) => {
  const { t } = useTranslation();
  const gridRef = useRef<SlickGrid1Ref<DerivedMeasurementRow> | null>(null);
  const estimateData = useSelector((state: RootState) => state.estimateData);

  const templateHelper = (name: string) => getMeasurementDetails(name);

  const handleRowSelection = () => {
    // KNOWN BUG, preserved verbatim: the grid ref is dereferenced with no guard here, though
    // `derivedMeasurementsDataBound` below checks it. Erased assertions.
    const selectedRecords = gridRef
      .current!.slickGrid.getSelectedRows()
      .map((data: number) => gridRef.current!.slickGrid.getDataItem(data) as DerivedMeasurementRow)
      .filter((data) => data);
    derivedMeasurementsSelectingEvents(selectedRecords);
  };

  const derivedMeasurementsDataBound = () => {
    if (selectedMeasurements.length && gridRef.current) {
      const rowIndices: number[] = [];
      dataSource.forEach((measurement, index: number) => {
        if (selectedMeasurements.find((record) => record.id == measurement.id)) {
          rowIndices.push(index);
        }
      });
      // The `gridRef.current` guard above does not survive into this callback.
      gridRef.current.dataView.onRowsChanged.subscribe(function () {
        gridRef.current!.slickGrid.setSelectedRows(rowIndices);
      });
    }
  };

  const deriveMeasurementsNameTemplate: DerivedMeasurementFormatter = (data, value, a, b, params) =>
    templateHelper(params._name).name;

  const deriveMeasurementsQuantityTemplate: DerivedMeasurementFormatter = (data, val, a, b, params) => {
    const container = document.createElement("div");
    const root = createRoot(container);
    let factor = 1;
    const { type } = templateHelper(params._name);
    const { linearDisplayUnit, areaDisplayUnit, volumeDisplayUnit, countDisplayUnit, angleDisplayUnit } =
      estimateData as LoadedEstimateData;

    if (linearDisplayUnit.type.name === type) factor = linearDisplayUnit.factor;
    if (areaDisplayUnit.type.name === type) factor = areaDisplayUnit.factor;
    if (volumeDisplayUnit.type.name === type) factor = volumeDisplayUnit.factor;
    if (countDisplayUnit.type.name === type) factor = countDisplayUnit.factor;
    if (angleDisplayUnit.type.name === type) factor = angleDisplayUnit.factor;

    const displayQuantity = getDisplayQuantity({
      quantity: params._measurementValue * params._unitMultiplier,
      factor,
    });
    const value = displayQuantity ? displayQuantity.quantity! : 0;
    root.render(<div>{getEstimateNumberValueBasedOnSettings(value, estimateData, "measurement")}</div>);
    return container;
  };

  const deriveMeasurementsUnitTemplate: DerivedMeasurementFormatter = (data, value, a, b, params) => {
    let selectedSymbol: { name?: string } = {};
    const { type } = templateHelper(params._name);
    const { linearDisplayUnit, areaDisplayUnit, volumeDisplayUnit, countDisplayUnit, angleDisplayUnit } =
      estimateData as LoadedEstimateData;

    if (linearDisplayUnit.type.name === type) selectedSymbol = linearDisplayUnit.selectedSymbol;
    if (areaDisplayUnit.type.name === type) selectedSymbol = areaDisplayUnit.selectedSymbol;
    if (volumeDisplayUnit.type.name === type) selectedSymbol = volumeDisplayUnit.selectedSymbol;
    if (countDisplayUnit.type.name === type) selectedSymbol = countDisplayUnit.selectedSymbol;
    if (angleDisplayUnit.type.name === type) selectedSymbol = angleDisplayUnit.selectedSymbol;

    return selectedSymbol.name;
  };

  const deriveMeasurementColumns: DerivedMeasurementColumn[] = [
    {
      field: "_name",
      name: t("Name"),
      formatter: deriveMeasurementsNameTemplate,
      textAlign: "Left",
    },
    {
      field: "_measurementValue",
      name: t("Measurement"),
      formatter: deriveMeasurementsQuantityTemplate,
      textAlign: "Left",
    },
    {
      field: "units",
      name: t("Units"),
      formatter: deriveMeasurementsUnitTemplate,
      textAlign: "Left",
    },
  ];

  return (
    <div className="overflow-auto h-full w-full">
      <SlickGrid1
        ref={gridRef}
        showPageCount={false}
        columnDefinitions={deriveMeasurementColumns as ValidatedColumnDefinition<DerivedMeasurementRow>[]}
        rowHeight={50}
        dataset={dataSource}
        onSelectedRowIdsChanged={handleRowSelection}
        onReactGridCreated={derivedMeasurementsDataBound}
        hideNoDataIllustration
      />
    </div>
  );
};

export default DeriveMeasurements;
